import { useCallback } from "react";
import { openHttpsUrl } from "@/lib/openExternalUrl";
import {
  dialStorePhone,
  getGoogleMapsSearchUrl,
  getStorePhoneLabel,
} from "@/lib/storeInfo";

type Props = {
  className?: string;
  /** 横並び（マップ・電話・予約）。未指定は従来の縦並び */
  layout?: "vertical" | "row";
  /** 予約（ホットペッパー等）を開く。`layout="row"` のとき 3 列目に表示 */
  onReserveClick?: () => void;
  reserveLabel?: string;
};

const compactBtnClass =
  "flex-1 min-w-0 flex flex-col items-center justify-center gap-1.5 py-3 px-1.5 rounded-2xl cursor-pointer transition-all duration-200 active:scale-[0.98]";

/**
 * VITE_STORE_MAP_QUERY / VITE_STORE_PHONE がどちらも空で、予約もなければ何も描画しない。
 */
export default function ShopContactSection({
  className = "",
  layout = "vertical",
  onReserveClick,
  reserveLabel = "予約",
}: Props) {
  const storeMapUrl = getGoogleMapsSearchUrl();
  const storePhoneLabel = getStorePhoneLabel();

  const openStoreMap = useCallback(() => {
    const url = getGoogleMapsSearchUrl();
    if (url) void openHttpsUrl(url);
  }, []);

  const showReserve = typeof onReserveClick === "function";
  if (!storeMapUrl && !storePhoneLabel && !showReserve) return null;

  if (layout === "row") {
    const rowButtons = [
      storeMapUrl ? (
        <button
          key="map"
          type="button"
          onClick={openStoreMap}
          className={compactBtnClass}
          style={{
            background: "rgba(212,175,55,0.1)",
            border: "1px solid rgba(212,175,55,0.3)",
          }}
          title="Googleマップで場所を見る"
        >
          <i className="ri-map-pin-line text-2xl shrink-0" style={{ color: "#d4af37" }}></i>
          <span
            className="text-[11px] font-bold tracking-wide text-center leading-tight break-words w-full px-0.5"
            style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
          >
            マップ
          </span>
        </button>
      ) : null,
      storePhoneLabel ? (
        <button
          key="phone"
          type="button"
          onClick={() => dialStorePhone()}
          className={compactBtnClass}
          style={{
            background: "rgba(212,175,55,0.1)",
            border: "1px solid rgba(212,175,55,0.3)",
          }}
          title={`電話する（${storePhoneLabel}）`}
        >
          <i className="ri-phone-line text-2xl shrink-0" style={{ color: "#d4af37" }}></i>
          <span
            className="text-[11px] font-bold tracking-wide text-center leading-tight break-words w-full px-0.5"
            style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
          >
            電話
          </span>
        </button>
      ) : null,
      showReserve ? (
        <button
          key="reserve"
          type="button"
          onClick={onReserveClick}
          className={compactBtnClass}
          style={{
            background: "rgba(212,175,55,0.1)",
            border: "1px solid rgba(212,175,55,0.3)",
          }}
          title="ホットペッパービューティーで予約"
        >
          <i className="ri-external-link-line text-2xl shrink-0" style={{ color: "#d4af37" }}></i>
          <span
            className="text-[11px] font-bold tracking-wide text-center leading-tight break-words w-full px-0.5"
            style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
          >
            {reserveLabel}
          </span>
        </button>
      ) : null,
    ].filter(Boolean);

    return (
      <div className={["space-y-2", className].filter(Boolean).join(" ")}>
        <div className="flex items-center gap-3">
          <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
          <span
            className="text-xs tracking-widest"
            style={{ color: "rgba(212,175,55,0.5)", fontFamily: "Montserrat, sans-serif" }}
          >
            SHOP
          </span>
          <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
        </div>
        <div className="flex flex-row gap-2 w-full">{rowButtons}</div>
      </div>
    );
  }

  return (
    <div className={["space-y-2", className].filter(Boolean).join(" ")}>
      <div className="flex items-center gap-3">
        <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
        <span
          className="text-xs tracking-widest"
          style={{ color: "rgba(212,175,55,0.5)", fontFamily: "Montserrat, sans-serif" }}
        >
          SHOP
        </span>
        <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.2)" }} />
      </div>
      {storeMapUrl && (
        <button
          type="button"
          onClick={openStoreMap}
          className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl cursor-pointer transition-all duration-200 active:scale-95"
          style={{
            background: "rgba(212,175,55,0.1)",
            border: "1px solid rgba(212,175,55,0.3)",
          }}
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-map-pin-line text-xl" style={{ color: "#d4af37" }}></i>
          </div>
          <span
            className="text-sm font-bold tracking-wider whitespace-nowrap"
            style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
          >
            Googleマップでお店の場所を見る
          </span>
        </button>
      )}
      {storePhoneLabel && (
        <button
          type="button"
          onClick={() => dialStorePhone()}
          className="w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl cursor-pointer transition-all duration-200 active:scale-95"
          style={{
            background: "rgba(212,175,55,0.1)",
            border: "1px solid rgba(212,175,55,0.3)",
          }}
        >
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-phone-line text-xl" style={{ color: "#d4af37" }}></i>
          </div>
          <span
            className="text-sm font-bold tracking-wider whitespace-nowrap"
            style={{ color: "#f4e5c3", fontFamily: "Montserrat, sans-serif" }}
          >
            電話する（{storePhoneLabel}）
          </span>
        </button>
      )}
    </div>
  );
}
